import { useField } from "formik";
import { Key, useEffect } from "react";

import { SearchableSelect, SearchableSelectProps } from "./SearchableSelect";
import {
  InputWrapper,
  InputWrapperProps,
} from "../../components/inputs/_assets/input-wrapper/InputWrapper";

export interface SearchableSelectFieldProps<T>
  extends Omit<SearchableSelectProps<T>, "onChange">,
    Omit<InputWrapperProps, "children"> {
  value?: string;
  name: string;
  label: string;
  info?: React.ReactNode;
  testID?: string;
  onSelectionChange?: (e: Key) => any;
  allowNull?: boolean;
}

/**
 * @deprecated use SearchableSelectFormikFieldRAC instead - based on React Aria Components
 */
export function SearchableSelectField<T extends object>({
  name,
  value,
  span = "auto",
  onSelectionChange,
  testID,
  allowNull = true,
  ...props
}: SearchableSelectFieldProps<T>) {
  const [field, meta, helpers] = useField(name);

  const handleSelectionChange = (key: Key) => {
    let value = key;
    if (value === null && !allowNull) {
      value = "";
    }
    if (onSelectionChange) onSelectionChange(value);

    // we are forcing touched true onBlur so
    // we need to clear error onSelectionChange
    helpers.setError("");

    setTimeout(() => {
      helpers.setValue(value);
    }, 0);
  };

  useEffect(() => {
    const items = props.children as JSX.Element[] | undefined;

    if (!Array.isArray(items) || items.find(({ key }) => key === field.value)) {
      return;
    }

    // Find correct item by text match and update form value
    const selectedItemKey =
      items.find(({ props }) => props.textValue === field.value)?.key ?? "";
    if (selectedItemKey) helpers.setValue(selectedItemKey);
    // This is disabled atm.  If it's not, we're getting endless calls to the call back func.
    // This is being caused by the helpers func from the formik hook changing across renders.
    // The fix is to upgrade formik which we'll do at some point.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <InputWrapper testID={`${testID}-container`} span={span}>
      <SearchableSelect
        testID={testID}
        validationState={meta.touched && meta.error ? "invalid" : "valid"}
        {...props}
        errors={meta.touched && meta.error}
        onBlur={() => {
          // this is required as react-aria is overwriting onBlur with name -> aria id
          helpers.setTouched(true);
        }}
        selectedKey={field.value || ""}
        onSelectionChange={handleSelectionChange}
      />
    </InputWrapper>
  );
}
